import { api } from '@/lib/api-client';

export type ReportKind = 'volume' | 'sla' | 'agents' | 'csat' | 'rca';

export interface ReportRange {
  from: string;
  to: string;
}

export interface BreakdownRow {
  key: string | null;
  label: string | null;
  count: number;
}

export interface VolumeReport {
  total: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  byGroup: Record<string, number>;
  byDate: Record<string, number>;
  byAgent: BreakdownRow[];
  byProduct: BreakdownRow[];
  byCountry: Record<string, number>;
  lifecycleByDate: {
    created: Record<string, number>;
    resolved: Record<string, number>;
    reopened: Record<string, number>;
  };
  from: string;
  to: string;
}

export interface SlaSummary {
  met: number;
  breached: number;
  total: number;
  rate: number;
}

export interface SlaBreakdownRow {
  key: string | null;
  label: string | null;
  firstResponse: SlaSummary;
  resolution: SlaSummary;
  averageFirstResponseSeconds: number;
  averageResolutionSeconds: number;
}

export interface SlaReport {
  firstResponse: SlaSummary;
  resolution: SlaSummary;
  averageFirstResponseSeconds: number;
  averageResolutionSeconds: number;
  byPriority: SlaBreakdownRow[];
  byGroup: SlaBreakdownRow[];
  byAgent: SlaBreakdownRow[];
  from: string;
  to: string;
}

export interface AgentPerformanceRow {
  id: string;
  name: string;
  email: string;
  assigned: number;
  resolved: number;
  replies: number;
  averageFirstResponseSeconds: number;
  averageResolutionSeconds: number;
}

export interface AgentPerformanceReport {
  data: AgentPerformanceRow[];
  from: string;
  to: string;
}

export interface CsatSurvey {
  id: string;
  rating: number;
  comment: string | null;
  createdAt: string;
  ticketNumber: number;
  subject: string;
}

export interface CsatReport {
  score: number;
  total: number;
  satisfied: number;
  averageRating: number;
  byDate: Record<string, number>;
  byGroup: BreakdownRow[];
  byAgent: BreakdownRow[];
  surveys: CsatSurvey[];
  from?: string;
  to?: string;
}

export interface RcaReport {
  byCategory: Record<string, number>;
  bySubcategory: Record<string, number>;
  from: string;
  to: string;
}

export interface ReportDataByKind {
  volume: VolumeReport;
  sla: SlaReport;
  agents: AgentPerformanceReport;
  csat: CsatReport;
  rca: RcaReport;
}

type Envelope<T> = { data: T };

export function validateReportRange(range: ReportRange): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(range.from) || !/^\d{4}-\d{2}-\d{2}$/.test(range.to)) {
    return 'Choose valid start and end dates.';
  }
  const from = Date.parse(`${range.from}T00:00:00Z`);
  const to = Date.parse(`${range.to}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 'Choose valid start and end dates.';
  if (to < from) return 'The end date must be on or after the start date.';
  const days = Math.floor((to - from) / 86400000) + 1;
  if (days > 90) return 'Reports support a maximum date range of 90 days.';
  return null;
}

export function defaultReportRange(): ReportRange {
  const today = new Date();
  const from = new Date(today.getTime() - 30 * 86400000);
  return { from: from.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) };
}

export async function fetchReport<K extends ReportKind>(
  kind: K,
  range: ReportRange,
  signal?: AbortSignal,
): Promise<ReportDataByKind[K]> {
  const options = { params: { from: range.from, to: range.to }, signal };
  if (kind === 'volume') {
    const response = await api.get<Envelope<VolumeReport>>('/reports/volume', options);
    return response.data as ReportDataByKind[K];
  }
  if (kind === 'sla') {
    const response = await api.get<Envelope<SlaReport>>('/reports/sla', options);
    return response.data as ReportDataByKind[K];
  }
  if (kind === 'agents') {
    return await api.get<AgentPerformanceReport>('/reports/agent-performance', options) as ReportDataByKind[K];
  }
  if (kind === 'csat') {
    return await api.get<CsatReport>('/reports/csat', options) as ReportDataByKind[K];
  }
  return await api.get<RcaReport>('/reports/rca', options) as ReportDataByKind[K];
}
